import moment from "moment";
import intl from "react-intl-universal";
import { generateQueryParams } from "utils/custom-functions";
import { formatNumber } from "utils/format";
import { isUseFilterDefault } from "utils/user";
import type {
  IProductionPlanSupplier,
  IProductionPlanSupplierMutationResponse,
  ProductionPlanSupplierFilters,
  ProductionPlanSupplierOption,
} from "./types";

const PRODUCTION_PLAN_SUPPLIER_DATE_FORMAT = "YYYY-MM-DD";
const PRODUCTION_PLAN_SUPPLIER_MAX_RANGE_DAYS = 29;

export const productionPlanSupplierStorageKey = "filtersProductionPlanSupplier";
export const getProductionPlanSupplierUseFilterDefault = () => Boolean(isUseFilterDefault());

const normalizeOptionList = (
  value: ProductionPlanSupplierOption[] | null | undefined
): ProductionPlanSupplierOption[] => (Array.isArray(value) ? value : []);

const formatDate = (value: moment.Moment) => value.format(PRODUCTION_PLAN_SUPPLIER_DATE_FORMAT);

export const getProductionPlanSupplierDefaultDateRange = (baseDate = moment()) => ({
  StartDate: formatDate(baseDate),
  EndDate: formatDate(baseDate.clone().add(PRODUCTION_PLAN_SUPPLIER_MAX_RANGE_DAYS, "days")),
});

export const createProductionPlanSupplierFilters = (
  overrides: Partial<ProductionPlanSupplierFilters> = {}
): ProductionPlanSupplierFilters => {
  const defaultDateRange = getProductionPlanSupplierDefaultDateRange();

  return {
    StartDate: overrides.StartDate ?? defaultDateRange.StartDate,
    EndDate: overrides.EndDate ?? defaultDateRange.EndDate,
    SupplierGroups: normalizeOptionList(overrides.SupplierGroups),
    Suppliers: normalizeOptionList(overrides.Suppliers),
    Materials: normalizeOptionList(overrides.Materials),
    Families: normalizeOptionList(overrides.Families),
    LabeledFamilies: normalizeOptionList(overrides.LabeledFamilies),
    _page: overrides._page ?? 0,
    _size: overrides._size ?? 10,
  };
};

export const productionPlanSupplierFiltersDefault = createProductionPlanSupplierFilters();

export const normalizeProductionPlanSupplierFilters = (
  filters: Partial<ProductionPlanSupplierFilters> | null | undefined
) => createProductionPlanSupplierFilters(filters ?? {});

const removeTrailingAmpersand = (query: string) => query.replace(/&$/, "");

export const getProductionPlanSupplierFiltersValidationError = (
  filters: ProductionPlanSupplierFilters
) => {
  if (!filters.StartDate || !filters.EndDate) {
    return intl.get("productionPlanSupplier.validation.dateRangeRequired");
  }

  const startDate = moment(filters.StartDate, PRODUCTION_PLAN_SUPPLIER_DATE_FORMAT, true);
  const endDate = moment(filters.EndDate, PRODUCTION_PLAN_SUPPLIER_DATE_FORMAT, true);

  if (!startDate.isValid() || !endDate.isValid()) {
    return intl.get("productionPlanSupplier.validation.invalidDateRange");
  }

  if (startDate.isAfter(endDate)) {
    return intl.get("productionPlanSupplier.validation.invalidDateRange");
  }

  if (endDate.diff(startDate, "days") > PRODUCTION_PLAN_SUPPLIER_MAX_RANGE_DAYS) {
    return intl.get("productionPlanSupplier.validation.maxDateRange");
  }

  return null;
};

const getFilterIds = (value: ProductionPlanSupplierOption[]) => value.map((item) => item.Id);

export const buildProductionPlanSupplierGridQuery = (
  filters: ProductionPlanSupplierFilters
): string =>
  removeTrailingAmpersand(
    generateQueryParams({
      "Filters.StartDate": filters.StartDate,
      "Filters.EndDate": filters.EndDate,
      "Filters.Suppliers": getFilterIds(filters.Suppliers),
      "Filters.Materials": getFilterIds(filters.Materials),
      "Filters.FamilyIds": getFilterIds(filters.Families),
      "Filters.LabeledFamilyIds": getFilterIds(filters.LabeledFamilies),
      "Filters.UseFilterDefault": getProductionPlanSupplierUseFilterDefault(),
      _page: filters._page,
      _size: filters._size,
    })
  );

export const buildProductionPlanSupplierExportQuery = (
  filters: ProductionPlanSupplierFilters
): string =>
  removeTrailingAmpersand(
    generateQueryParams({
      "Filters.StartDate": filters.StartDate,
      "Filters.EndDate": filters.EndDate,
      "Filters.Suppliers": getFilterIds(filters.Suppliers),
      "Filters.Materials": getFilterIds(filters.Materials),
      "Filters.FamilyIds": getFilterIds(filters.Families),
      "Filters.LabeledFamilyIds": getFilterIds(filters.LabeledFamilies),
      "Filters.UseFilterDefault": getProductionPlanSupplierUseFilterDefault(),
    })
  );

export const buildProductionPlanSupplierImportPayload = (content: string) => ({
  content,
  useFilterDefault: getProductionPlanSupplierUseFilterDefault(),
});

export const getProductionPlanSupplierDates = (suppliers: IProductionPlanSupplier[]): string[] => {
  const dates = new Set<string>();

  suppliers.forEach((supplier) => {
    supplier.ProductionDates.forEach((productionDate) => {
      dates.add(productionDate.ProductionDate);
    });
  });

  return Array.from(dates).sort((left, right) => left.localeCompare(right));
};

export const formatProductionPlanSupplierNumericValue = (value: number | null | undefined) =>
  value === null || value === undefined ? "-" : formatNumber(value);

export const parseProductionPlanSupplierNumericValue = (value: string) => {
  const normalized = value.trim();

  if (!normalized) {
    return null;
  }

  const cleaned = normalized.replace(/\s/g, "").replace(/[^\d,.-]/g, "");
  const lastComma = cleaned.lastIndexOf(",");
  const lastDot = cleaned.lastIndexOf(".");
  let decimalSeparator: "," | "." | null = null;

  if (lastComma > lastDot) {
    decimalSeparator = ",";
  } else if (lastDot > -1) {
    decimalSeparator = ".";
  }

  let parsedValue = cleaned;

  if (decimalSeparator === ",") {
    parsedValue = parsedValue.replace(/\./g, "").replace(",", ".");
  } else if (decimalSeparator === ".") {
    parsedValue = parsedValue.replace(/,/g, "");
  } else {
    parsedValue = parsedValue.replace(/[^\d-]/g, "");
  }

  const numericValue = Number(parsedValue);

  return Number.isFinite(numericValue) ? numericValue : null;
};

export const applyProductionPlanSupplierMutationResponse = (
  suppliers: IProductionPlanSupplier[],
  response: IProductionPlanSupplierMutationResponse
) =>
  suppliers.map((supplier) => {
    if (supplier.Id !== response.Id) {
      return supplier;
    }

    const updatedProductionDates = [...supplier.ProductionDates];

    if (response.ProductionDate) {
      const productionDateIndex = updatedProductionDates.findIndex(
        (productionDate) => productionDate.ProductionDate === response.ProductionDate
      );

      if (productionDateIndex >= 0) {
        updatedProductionDates[productionDateIndex] = {
          ...updatedProductionDates[productionDateIndex],
          ProductionQuantity: response.ProductionQuantity,
        };
      } else {
        updatedProductionDates.push({
          ProductionDate: response.ProductionDate,
          ProductionQuantity: response.ProductionQuantity,
        });
      }

      updatedProductionDates.sort((left, right) =>
        left.ProductionDate.localeCompare(right.ProductionDate)
      );
    }

    return {
      ...supplier,
      CurrentStock: response.CurrentStock,
      Forecast: response.Forecast,
      LastUpdate: response.LastUpdate,
      ProductionDates: updatedProductionDates,
    };
  });

export const hasActiveProductionPlanSupplierFilters = (filters: ProductionPlanSupplierFilters) => {
  const defaultDateRange = getProductionPlanSupplierDefaultDateRange();

  return (
    filters.StartDate !== defaultDateRange.StartDate ||
    filters.EndDate !== defaultDateRange.EndDate ||
    filters.SupplierGroups.length > 0 ||
    filters.Suppliers.length > 0 ||
    filters.Materials.length > 0 ||
    filters.Families.length > 0
    || filters.LabeledFamilies.length > 0
  );
};

type RawOption = {
  id?: number | string;
  description?: string;
  Id?: number | string;
  Name?: string;
  Description?: string;
  MaterialNumber?: string | number;
  MaterialDescription?: string;
};

export const normalizeProductionPlanSupplierOptions = (
  options: RawOption[] | null | undefined
): ProductionPlanSupplierOption[] =>
  Array.isArray(options)
    ? options.map((option) => ({
      Id: option.Id ?? option.id ?? option.MaterialNumber ?? "",
      Description:
          option.description ??
          option.Description ??
          option.Name ??
          option.MaterialDescription ??
          String(option.MaterialNumber ?? option.Id ?? option.id ?? ""),
      }))
    : [];
